import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Error } from '../models/error';
import { isSeatHoldExpired } from './seat-hold-expired';
import { NzNotificationService } from 'ng-zorro-antd/notification';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  public constructor(private readonly _notifications: NzNotificationService) {}

  public intercept(
    request: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse) => {
        // Share-link 404s are an expected outcome (invalid/revoked invitation
        // link). The ViewOneComponent renders an inline message for them, so
        // suppress the generic toast here to avoid double-surfacing.
        const isShareLink404 =
          error.status === 404 &&
          /\/opn\/v1\/events\/share\//.test(request.url);

        // Wallet top-up surfaces its own tailored failure copy, which names the
        // precondition that failed. Without this the customer got two toasts for
        // one failure (QA finding NEW-10).
        const isHandledByCaller = /\/v1\/wallet-top-ups/.test(request.url);

        // The ticket-wallet balance read is speculative: My Tickets asks for a balance on every
        // ticket whose event runs wallets, and a ticket issued before that flag was turned on
        // legitimately has no wallet. The caller already treats that as "no wallet" rather than a
        // failure, so a toast here told the customer their account was broken because some of their
        // tickets were old. It currently arrives as a 500 rather than a 404, which is a separate
        // backend defect (QA finding BUG-10) — suppressed by route so this holds either way.
        const isSpeculativeWalletProbe = /\/client\/v1\/ticket-wallets/.test(request.url);

        // A seated payment refused because the seat hold lapsed. The checkout page answers it with its own
        // explicit refusal and sends the buyer back to choose seats, so a toast of the raw message
        // ("SEAT_HOLD_EXPIRED: ...") on top would only repeat it.
        const isRefusedByCaller = isSeatHoldExpired(error);

        if (!isShareLink404 && !isHandledByCaller && !isSpeculativeWalletProbe && !isRefusedByCaller) {
          const errorModel: Error = this.handleHttpError(error);
          this._notifications.error(errorModel.title || 'Error', errorModel.message);
        }
        return throwError(error);
      })
    );
  }

  private handleHttpError(error: HttpErrorResponse): Error {
    let errorResponse: any = {};
    try {
      errorResponse = typeof error.error === 'string' ? JSON.parse(error.error) : error.error;
    } catch (e) {
      errorResponse = error.error;
    }

    // A 4xx message is written for the person reading it — echo it. A 5xx body is an internal
    // failure chain and must never reach a user: the wallet-top-up fault, for instance, renders as
    // `[500] during [GET] to [http://merchants-service/pvt/internal/v1/ticket-wallets/ticket/{uid}]
    // [MerchantsModuleClient#getTicketWalletByTicketUid(String)]`, disclosing internal hostnames,
    // private endpoint paths and client method names (QA finding NEW-9). Fall through to the
    // status-based copy below instead.
    const serverMessageIsPresentable = error.status > 0 && error.status < 500;

    if (errorResponse && errorResponse.message && serverMessageIsPresentable) {
      return {
        title: errorResponse.title || 'Error',
        message: errorResponse.message
      };
    } else {
      switch (error.status) {
        case 0: {
          return {
            title: 'Failed to connect to the server',
            message:
              'Could not establish a connection to our server. Please try again in a bit'
          };
        }

        case 400: {
          return {
            title: 'Bad Request',
            message:
              'The request was invalid. Please check your input and try again.'
          };
        }

        case 401: {
          return {
            title: 'You are not authorized to perform this action',
            message:
              'Please login and try again. If the error continues, contact system administrator.'
          };
        }

        case 500: {
          return {
            title: 'There was a problem while processing your request',
            message:
              'Please try again later, or contact system administrator'
          };
        }
        case 504: {
          return {
            title: 'System not available to process your request at the moment.',
            message:
              'Please try again later. If the problem persist, please contact system admimistrator'
          };
        }

        default: {
          return {
            title: 'Failed to execute your request',
            message:
              'Please try again later, or contact system administrator'
          };
        }
      }
    }
  }
}
